/**
 * What a login screen does with a failure — decided once, here, for A3, A4, A7 and the
 * recovery form.
 *
 * ── WHAT THIS FILE USED TO DECIDE, AND WHY THAT CHANGED (W2-12) ──────────────
 *
 * It used to say: a surface renders a state when a screen is drawn for it AND
 * `states/matrix.ts` admits it there, and everything else renders NOTHING — the screen
 * stays exactly as drawn and the failure goes to the console. That was the right rule for
 * a build that could not write a line of Bangla and was gated at 2% on every pixel. It
 * also meant that on the door every customer walks through first, a person who asked for
 * five codes in an hour, or whose Turnstile token had gone stale, or who typed a digit
 * short, pressed "কোড পাঠান" and watched nothing happen at all.
 *
 * The founder delegated undrawn UI copy (2026-09-18, "nothing dummy left") and the
 * departures from A3/A4/A7 are recorded in `docs/deviations/W2-12.md`. So a failure on
 * this surface now gets one of three answers, and the rule for which is below:
 *
 *   A FULL FRAME, when what happened changes where the person is:
 *     ইন্টারনেট নেই          the request never arrived — the drawn state, with its retry;
 *     অ্যাকাউন্ট খোলা হয়েছে  a NEW number was let in with no code (docs/07 § 4) — a
 *                             session exists, and the one way on is forward;
 *     যাচাই অপেক্ষমাণ       an EXISTING number was told to wait and got nothing — the
 *                             drawn S9 card with the server's sentence, and Telegram.
 *
 *   A LINE UNDER THE CONTROL, when the person stays and tries again:
 *     wrong code          the server's own "কোডটি মেলেনি…", plus how many tries are left;
 *     throttled           the plain `429 {retry_after_seconds}` — an authored sentence;
 *     refused             any refusal that carries its own `message_bn` (the Turnstile
 *                         pair in `@oprappo/contracts § TURNSTILE_REFUSAL_BN`, say);
 *     unexplained         nothing the server said can be shown — a sentence the SCREEN
 *                         supplies, naming what did not happen ("কোড পাঠানো যায়নি"),
 *                         never the generic failure line this product forbids.
 *
 *   NOTHING, still, for the one case that is a bug and not a situation: a body the
 *   Worker's schema refused. The screen cannot send one (it checks the number first).
 *
 * The two frames' sentences and the two inline sentences written here are AUTHORED —
 * `AUTH_FRAME_COPY` below, `data-copy-status="authored"` in the DOM, listed in
 * `docs/copy-review/W2-12.md`. Every other sentence on these frames is the server's.
 *
 * ── PURE RENDER, RECORDING AT THE CATCH ──────────────────────────────────────
 *
 * Unchanged: `renderAuthFailure` and `InlineAnswer` write nothing to the console — a
 * render can happen twice. `noteFailure` is called once, where the failure is caught, and
 * records the ones a person could not have caused: an unexplained failure and a refusal.
 */
import type { ApiErrorBody, CopyStatus, ErrorCode } from '@oprappo/contracts';
import { ERROR_CODES } from '@oprappo/contracts';
import { ariaLabel } from '@oprappo/tokens';
import { Button } from '@oprappo/ui';
import { ApiError, NetworkError, UnmappedApiError } from '../api/client.js';
import { FRAME_BOX } from '../responsive/frame.js';
import { reportUnmapped } from '../states/unmapped.js';
import { resolveScreenState } from '../states/boundary.js';
import { OfflineState } from '../states/OfflineState.js';
import { BackChevron, StateFrame, TickGlyph, WARNING_BORDER } from '../states/chrome.js';
import { VERIFICATION_PENDING } from '../states/data.js';
import { retryAfterSeconds, toBanglaDigits } from './otp.js';

/* ═══════════════════════════════════════════════════════════ the copy ══ */

/**
 * The sentences this file renders that no frame draws and no server sends.
 * `copy_status: 'authored'` — written by W2-12, unreviewed; `docs/copy-review/W2-12.md`.
 * The two marked "sourced" are drawn elsewhere in `Oprappo P0 Screens.dc.html` and reused
 * so the person meets words they have already seen.
 */
export const AUTH_FRAME_COPY = {
  copy_status: 'authored' satisfies CopyStatus,
  admitted: {
    title_bn: 'আপনার অ্যাকাউন্ট খোলা হয়েছে',
    /** What a deferred session can and cannot do — founder decision 2026-09-18. */
    note_bn: 'যাচাই শেষ না হওয়া পর্যন্ত অ্যাপ ঘুরে দেখতে পারবেন। অর্ডার ও পেমেন্ট করা যাবে যাচাইয়ের পরে।',
    /** Sourced: A2's own button. */
    continue_bn: 'এগিয়ে যান',
  },
  identity_wait: {
    /** True of an existing identity only when it linked Telegram from inside its account. */
    telegram_note_bn: 'আগে টেলিগ্রাম যুক্ত করে থাকলে কোডটি টেলিগ্রামে নিতে পারেন।',
    telegram_bn: 'টেলিগ্রামে কোড নিন',
  },
  /** `{n}` is Bangla digits, and the only substitution. */
  attempts_left_bn: 'আর {n} বার চেষ্টা করা যাবে।',
  /** `{minutes}` is Bangla digits, and the only substitution. */
  throttled_bn: 'অনেকবার চেষ্টা করা হয়েছে। {minutes} মিনিট পরে আবার চেষ্টা করুন।',
} as const;

/** `{n}` → `৩`. One whole sentence with one number in it; nothing is composed. */
export function attemptsLeftBn(n: number): string {
  return AUTH_FRAME_COPY.attempts_left_bn.replace('{n}', toBanglaDigits(String(n)));
}

/** Seconds to whole minutes, rounded up so "wait" never undersells the wait. */
export function throttledBn(seconds: number): string {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return AUTH_FRAME_COPY.throttled_bn.replace('{minutes}', toBanglaDigits(String(minutes)));
}

/* ═══════════════════════════════════════════════ reading the failure ══ */

export interface FailureHandlers {
  /** The one way out the ইন্টারনেট নেই frame draws. */
  onRetry?: (() => void) | undefined;
  /** The back control on the যাচাই অপেক্ষমাণ frame. */
  onBack?: (() => void) | undefined;
  /** Where "you're in" goes on. Absent: `onBack`. */
  onContinue?: (() => void) | undefined;
  /** The Telegram option on the honest wait. Absent: the option is not drawn. */
  onTelegram?: (() => void) | undefined;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const err = (value as { error?: unknown }).error;
  if (typeof err !== 'object' || err === null) return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && (ERROR_CODES as readonly string[]).includes(code);
}

/**
 * The error envelope this failure carries, if any.
 *
 * A raw `ApiErrorBody` is accepted as well as a thrown `ApiError`, because one path
 * arrives inside a 200: `outcome: 'deferred'` carries its sentence as `notice`, and
 * re-enveloping that notice hands the states layer the server's own sentence in the
 * shape it reads.
 */
export function failureBody(cause: unknown): ApiErrorBody | null {
  if (cause instanceof ApiError) return cause.body;
  if (isApiErrorBody(cause)) return cause;
  if (cause instanceof Admitted) return cause.body;
  return null;
}

/**
 * Turn `outcome: 'deferred'`'s notice into the envelope the states layer reads, or
 * `null` if the Worker ever sends a code this build has no state for.
 */
export function noticeAsErrorBody(notice: {
  code: string;
  message_bn: string;
  message_en: string;
}): ApiErrorBody | null {
  if (!(ERROR_CODES as readonly string[]).includes(notice.code)) return null;
  return {
    error: {
      code: notice.code as ErrorCode,
      message_bn: notice.message_bn,
      message_en: notice.message_en,
      // The 200 carries no reference, and this outcome has none to read aloud, so it
      // is empty rather than a number invented here.
      reference: '',
    },
  };
}

/**
 * A NEW identity, let in with a session while no channel could carry its code
 * (`outcome: 'deferred'`, docs/07 § 4).
 *
 * Its own type rather than the notice re-enveloped, because the two answers that share
 * the Worker's sentence must not be confused: this one holds a session and goes ON, an
 * existing identity told to wait holds nothing and goes BACK. They used to render the
 * same frame and differ only in where its back arrow led — which left the person who
 * was let in with no drawn way forward.
 */
export class Admitted {
  readonly messageBn: string;
  readonly body: ApiErrorBody | null;
  constructor(notice: { code: string; message_bn: string; message_en: string }) {
    this.messageBn = notice.message_bn;
    this.body = noticeAsErrorBody(notice);
  }
}

/** Which drawn state answers this failure on the onboarding surface, if any. */
export function drawnStateFor(cause: unknown): 'offline' | 'verification_pending' | null {
  // A request that never arrived is ইন্টারনেট নেই, which onboarding draws. The client
  // says so itself: `NetworkError` "maps to E_OFFLINE's screen".
  if (cause instanceof NetworkError) return 'offline';

  const body = failureBody(cause);
  if (body === null) return null;

  // The same function the route boundary uses, so `states/matrix.ts` decides here
  // exactly as it decides there.
  const resolved = resolveScreenState('', 'onboarding', body);
  if (resolved === null) return null;
  if (resolved.key === 'verification_pending') return 'verification_pending';
  if (resolved.key === 'offline') return 'offline';
  return null;
}

/** What the server said about tries left on this code, when it said anything. */
function attemptsLeftOf(body: ApiErrorBody): number | null {
  // The Worker puts `attempts_left` beside `error`, not inside it (`middleware/errors.ts
  // § clientExtra`), and the transport keeps the whole payload as the body.
  const raw = (body as unknown as { attempts_left?: unknown }).attempts_left;
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 ? raw : null;
}

/** The answer a login screen gives a failure. See the header for the three kinds. */
export type AuthAnswer =
  | { kind: 'offline' }
  | { kind: 'admitted'; message_bn: string }
  | { kind: 'identity_wait'; message_bn: string }
  | { kind: 'wrong_code'; message_bn: string; attempts_left: number | null }
  | { kind: 'throttled'; retry_after_seconds: number }
  | { kind: 'refused'; message_bn: string }
  | { kind: 'unexplained' };

/** The kinds that are a line under the control rather than a frame. */
export type InlineAuthAnswer = Extract<
  AuthAnswer,
  { kind: 'wrong_code' | 'throttled' | 'refused' | 'unexplained' }
>;

export function answerFor(cause: unknown): AuthAnswer | null {
  if (cause === null || cause === undefined) return null;
  if (cause instanceof Admitted) return { kind: 'admitted', message_bn: cause.messageBn };
  if (drawnStateFor(cause) === 'offline') return { kind: 'offline' };

  const wait = retryAfterSeconds(cause);
  if (wait !== null) return { kind: 'throttled', retry_after_seconds: wait };

  const body = failureBody(cause);
  if (body === null) return { kind: 'unexplained' };

  if (body.error.code === 'E_VERIFICATION_PENDING') {
    // `/otp/verify` refuses a code as a 422 with `attempts_left`; `/otp/request` tells an
    // existing identity to wait as a 403 with `retry`. Same code, opposite situations.
    const left = attemptsLeftOf(body);
    if (left !== null || (cause instanceof ApiError && cause.status === 422)) {
      return { kind: 'wrong_code', message_bn: body.error.message_bn, attempts_left: left };
    }
    return { kind: 'identity_wait', message_bn: body.error.message_bn };
  }

  // A refusal with its own sentence is shown, whatever its code — the sentence was
  // written for exactly this. An empty one (a body refusal, `copy_missing`) is not.
  if (body.error.message_bn.trim() !== '') return { kind: 'refused', message_bn: body.error.message_bn };
  return { kind: 'unexplained' };
}

/** Is this answer a line under the control? */
export function isInline(answer: AuthAnswer | null): answer is InlineAuthAnswer {
  return (
    answer !== null &&
    (answer.kind === 'wrong_code' ||
      answer.kind === 'throttled' ||
      answer.kind === 'refused' ||
      answer.kind === 'unexplained')
  );
}

/**
 * Write a failure down where a person can find it. Called once, at the catch.
 *
 * `console.error` and not a re-throw: an exception escaping a click handler takes the
 * route down with it, and somebody who pressed a button deserves the screen they were
 * on rather than a blank page.
 */
export function recordUnrenderableFailure(where: string, cause: unknown): void {
  const tail = 'The screen shows a line for it; see auth/failure.tsx.';
  if (typeof cause === 'string') {
    console.error(`[auth] ${where}: ${cause} ${tail}`);
    return;
  }
  if (cause instanceof UnmappedApiError) {
    // ONE CHANNEL FOR THIS ONE. `states/unmapped.ts` records the status and the payload
    // AND keeps them where an operator can read them back on a support call.
    reportUnmapped(where, cause);
    return;
  }
  if (cause instanceof ApiError) {
    console.error(
      `[auth] ${where}: ${cause.code} (${cause.reference}) — ${cause.messageEn}. ${tail}`,
      cause.body,
    );
    return;
  }
  console.error(`[auth] ${where}: unexpected failure. ${tail}`, cause);
}

/**
 * Record the failure if the person could not have caused it, and hand it back for the
 * screen to hold. One call at every catch site; the return value is what goes into state.
 *
 * A throttle, a wrong code, a lost connection and the two waits are the product working
 * and are not logged. A refusal and an unexplained failure are.
 */
export function noteFailure(where: string, cause: unknown): unknown {
  const answer = answerFor(cause);
  if (answer === null || answer.kind === 'refused' || answer.kind === 'unexplained') {
    recordUnrenderableFailure(where, cause);
  }
  return cause;
}

/* ══════════════════════════════════════════════════════════ the frames ══ */

function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/**
 * "You're in" — a new number let in while no channel could carry its code.
 *
 * Undrawn. Built from the state frames' own vocabulary, as `auth/SignInPrompt.tsx` is:
 * the sand frame, § ৯'s tick, `600 22px/1.25` for the heading (display band), the
 * server's sentence at `15px/1.75`, and one full-width primary button at the foot.
 * No back control: the session exists, and the way on is forward.
 */
function AdmittedFrame({
  messageBn,
  onContinue,
}: {
  messageBn: string;
  onContinue?: (() => void) | undefined;
}): JSX.Element {
  const copy = AUTH_FRAME_COPY.admitted;
  return (
    <div data-auth-frame="admitted" className={FRAME_BOX + ' bg-sand border border-border px-[24px] pt-[48px] pb-[24px]'}>
      <div className="box-content w-[56px] h-[56px] border-2 border-success flex items-center justify-center mb-[22px]">
        <TickGlyph />
      </div>
      <h1
        data-copy-status={AUTH_FRAME_COPY.copy_status}
        className="m-0 font-anek font-semibold text-[22px] leading-[1.25] text-ink"
      >
        {copy.title_bn}
      </h1>
      {/* The server's sentence — how long the wait is. Never written here. */}
      {said(messageBn) ? (
        <div role="status" className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[10px]">
          {messageBn}
        </div>
      ) : null}
      <div
        data-copy-status={AUTH_FRAME_COPY.copy_status}
        className="font-anek font-normal text-[14px] leading-[1.75] text-muted mt-[8px]"
      >
        {copy.note_bn}
      </div>
      <div className="flex-1" />
      <Button tone="primary" size="lg" onClick={onContinue}>
        {copy.continue_bn}
      </Button>
    </div>
  );
}

/**
 * The honest wait — an EXISTING number that no channel could reach, which gets nothing
 * (`otp.ts § deferIdentity` says why that asymmetry is the security fix).
 *
 * `P0 States § ৯`'s own card — its title, its chip, the server's sentence — as
 * `states/VerificationPendingState.tsx` draws it, plus the one thing that can help now:
 * a code over Telegram, which reaches an existing account only when it linked Telegram
 * from inside it. The note says exactly that, so the button is not a promise.
 */
function IdentityWaitFrame({
  messageBn,
  onBack,
  onTelegram,
}: {
  messageBn: string;
  onBack?: (() => void) | undefined;
  onTelegram?: (() => void) | undefined;
}): JSX.Element {
  const d = VERIFICATION_PENDING;
  const copy = AUTH_FRAME_COPY.identity_wait;
  return (
    <StateFrame stateKey="verification_pending">
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div role="status" className={'bg-white border ' + WARNING_BORDER + ' border-l-[3px] border-l-amber p-[15px]'}>
          <div className="flex justify-between items-start gap-[11px]">
            <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{d.title_bn}</div>
            <div className="border border-amber-text px-[8px] py-[2px] font-anek font-medium text-[13px] leading-[1.75] text-amber-text flex-none">
              {d.chip_bn}
            </div>
          </div>
          {said(messageBn) ? (
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[8px]">{messageBn}</div>
          ) : null}
        </div>

        {onTelegram === undefined ? null : (
          <div data-copy-status={AUTH_FRAME_COPY.copy_status} className="mt-[20px]">
            <div className="font-anek font-normal text-[14px] leading-[1.75] text-body mb-[11px]">
              {copy.telegram_note_bn}
            </div>
            <Button tone="secondary" size="md" onClick={onTelegram}>
              {copy.telegram_bn}
            </Button>
          </div>
        )}
      </div>
    </StateFrame>
  );
}

/**
 * The frame for this failure, or `null` when the answer is a line under the control —
 * in which case the caller renders its screen with `InlineAnswer` in it.
 */
export function renderAuthFailure(
  cause: unknown,
  handlers: FailureHandlers = {},
): JSX.Element | null {
  const answer = answerFor(cause);
  switch (answer?.kind) {
    case 'offline':
      return <OfflineState onRetry={handlers.onRetry} />;
    case 'admitted':
      return <AdmittedFrame messageBn={answer.message_bn} onContinue={handlers.onContinue ?? handlers.onBack} />;
    case 'identity_wait':
      return (
        <IdentityWaitFrame messageBn={answer.message_bn} onBack={handlers.onBack} onTelegram={handlers.onTelegram} />
      );
    default:
      return null;
  }
}

/**
 * A line under the control, in § 02's error-line type — `500 13px/1.75` in `#8E2018`
 * (`packages/ui/src/Input.tsx § error`) — announced as an alert.
 *
 * `unexplainedBn` is the screen's own sentence for "this did not happen", because only
 * the screen knows what "this" was: a code not sent, a code not checked, a request not
 * delivered. A line the server wrote carries no copy marker; a line written here is
 * marked `authored`.
 */
export function InlineAnswer({
  answer,
  unexplainedBn,
  className,
}: {
  answer: AuthAnswer | null;
  unexplainedBn: string;
  className?: string | undefined;
}): JSX.Element | null {
  if (!isInline(answer)) return null;
  const lines: Array<{ text: string; authored: boolean }> = [];
  switch (answer.kind) {
    case 'wrong_code':
      if (said(answer.message_bn)) lines.push({ text: answer.message_bn, authored: false });
      if (answer.attempts_left !== null && answer.attempts_left > 0) {
        lines.push({ text: attemptsLeftBn(answer.attempts_left), authored: true });
      }
      break;
    case 'throttled':
      lines.push({ text: throttledBn(answer.retry_after_seconds), authored: true });
      break;
    case 'refused':
      lines.push({ text: answer.message_bn, authored: false });
      break;
    case 'unexplained':
      lines.push({ text: unexplainedBn, authored: true });
      break;
  }
  if (lines.length === 0) return null;
  return (
    <div
      role="alert"
      data-auth-answer={answer.kind}
      className={'font-anek font-medium text-[13px] leading-[1.75] text-danger-ink' + (className ? ' ' + className : '')}
    >
      {lines.map((line) => (
        <div key={line.text} {...(line.authored ? { 'data-copy-status': AUTH_FRAME_COPY.copy_status } : {})}>
          {line.text}
        </div>
      ))}
    </div>
  );
}
